import {IUserStatistic} from '@/stores/ItemsStore/ItemsStore.types';

import {declintRu} from '@specials/utils';

import {BILLION_ABBR, MEASURE_TEMPLATE_VALUE, MILLION_ABBR} from '@/constants';
import {IStatisticConfig, StatisticValidationConfig} from '@/resources';
import {MB_SEC_ADDITIONAL_INFO} from '@/resources/statisticContent';
import {FrontendError} from '@/services/ErrorService/FrontendError';
import {StatisticItemValidationType} from '@/types';

import {validationRules} from './validationRules';

export type TStatisticMapperInput = {
  singleItemStatistic: IUserStatistic;
  singleItemStatisticContent: IStatisticConfig;
  singleItemStatisticValidation?: StatisticValidationConfig;
  logError: (error: FrontendError) => void;
  shouldLogError?: boolean;
};

export type TStatisticMapperOutput = {
  mainStatisticTitle: string;
  mainStatisticDescription: string;
  extraStatisticDescription: string;
};

type PluralizeInput = {
  plurals: string[];
  text: string;
  value: string;
  logErrorCb: VoidFunction;
};

export class StatisticMapper {
  static map(input: TStatisticMapperInput): TStatisticMapperOutput | null {
    const {singleItemStatisticValidation, singleItemStatisticContent, singleItemStatistic, logError, shouldLogError} =
      input;

    // трансформируем данные из статистики в значения, которые будем подставлять
    let transformedValueFromStatistic: string | null = singleItemStatistic.value;

    if (singleItemStatisticValidation) {
      transformedValueFromStatistic = this.transformAndValidateStatisticData(
        singleItemStatistic,
        singleItemStatisticValidation,
        logError,
        shouldLogError,
      );
    } else {
      // логируем отсутствие конфига валидации для параметра
      const error = FrontendError.createFindStatisticValidationRuleError(
        singleItemStatistic.metricDesc,
        singleItemStatistic.value,
      );
      logError(error);
    }

    // убираем статистику, если ее значение невалидно
    if (transformedValueFromStatistic === null) {
      return null;
    }

    const paramName = singleItemStatisticContent.parameterName;
    const mainPlurals = singleItemStatisticContent.content.main.plurals;
    const descriptionPlurals = singleItemStatisticContent.content.description.plurals;

    // callback логирования ошибки парсинга числа в pluralizer
    const error = FrontendError.createParsePluralNumberValueError(
      singleItemStatistic.metricDesc,
      transformedValueFromStatistic,
    );

    const pluralizeErrorCb = (): void => {
      logError(error);
    };

    let mainStatisticTitle = singleItemStatisticContent.content.main.title.replaceAll(
      `$${paramName}`,
      transformedValueFromStatistic,
    );

    // добавление в заголовок склонение единицы измерения для числовых значений
    mainStatisticTitle = this.pluralizeValue({
      plurals: mainPlurals,
      text: mainStatisticTitle,
      value: transformedValueFromStatistic,
      logErrorCb: pluralizeErrorCb,
    });

    const mainStatisticDescription = this.addMbSecAdditionalText(
      singleItemStatistic,
      singleItemStatisticContent.content.main.text,
      pluralizeErrorCb,
    );

    let extraTransformedValueFromStatistic: string | null = transformedValueFromStatistic;

    if (singleItemStatisticValidation) {
      extraTransformedValueFromStatistic = this.transformLongStringStatisticValue(
        singleItemStatistic,
        singleItemStatisticValidation,
        transformedValueFromStatistic,
      );
    }

    let extraStatisticDescription = singleItemStatisticContent.content.description.text.replaceAll(
      `$${paramName}`,
      extraTransformedValueFromStatistic,
    );

    // добавление в описание склонение единицы измерения для числовых значений
    extraStatisticDescription = this.pluralizeValue({
      plurals: descriptionPlurals,
      text: extraStatisticDescription,
      value: extraTransformedValueFromStatistic,
      logErrorCb: pluralizeErrorCb,
    });

    extraStatisticDescription = this.addMbSecAdditionalText(
      singleItemStatistic,
      extraStatisticDescription,
      pluralizeErrorCb,
    );

    return {
      mainStatisticTitle,
      mainStatisticDescription,
      extraStatisticDescription,
    };
  }

  private static transformLongStringStatisticValue(
    statisticData: IUserStatistic,
    statisticValidation: StatisticValidationConfig,
    defaultStatisticValue: string,
  ): string {
    const statistic = {...statisticData};
    const rules = statisticValidation.rules;
    let statisticValue = statistic.value;

    const maxStringLengthRule = rules.find((rule) => rule.name === StatisticItemValidationType.MaxStringLength);

    // если в списке правил нет правила MaxStringLength,
    // то возвращаем дефолтное значение
    if (!maxStringLengthRule) {
      return defaultStatisticValue;
    }

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];

      // игнорируем правило MaxStringLength
      if (rule.name === StatisticItemValidationType.MaxStringLength) {
        continue;
      }
      // т.к. ранее уже был обработан вариант, когда validationRules вернет null,
      // то statisticValue будет строковым значением
      statisticValue = validationRules[rule.name](statistic, rule, () => {}) ?? defaultStatisticValue;
    }

    return statisticValue;
  }

  private static transformAndValidateStatisticData(
    statisticData: IUserStatistic,
    statisticValidation: StatisticValidationConfig,
    logError: (error: FrontendError) => void,
    shouldLogError?: boolean,
  ): string | null {
    const statistic = {...statisticData};
    let statisticValue: string | null = statistic.value;

    // валидируем значение в соответствии с правилами, указанными в его конфиге.
    // если значение не валидно, то правило возвращает null.
    // если какое-то из правил вернуло null, то дальнейшие проверки не проводятся
    // и в лог отправляется ошибка
    const rules = statisticValidation.rules;

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      statisticValue = validationRules[rule.name](statistic, rule, logError, shouldLogError);

      // если статистика невалидна, прерываем цикл дальнейших проверок.
      // иначе сохраняем преобразованное значение и предаем его дальше
      if (statisticValue === null) {
        break;
      } else {
        statistic.value = statisticValue;
      }
    }

    return statisticValue;
  }

  private static addMbSecAdditionalText = (
    singleItemStatistic: IUserStatistic,
    initialText: string,
    logErrorCb: VoidFunction,
  ): string => {
    if (singleItemStatistic.metricDesc === MB_SEC_ADDITIONAL_INFO.fieldName) {
      const {text, plurals, durationInMin, parameterName} = MB_SEC_ADDITIONAL_INFO;
      let formattedText: string;

      const getCalculatedStatisticValue = (value: string): number => {
        const numberValue = Number.isNaN(Number(value)) ? 0 : Number(value);

        return Math.floor(numberValue / (durationInMin * 60));
      };

      const calculatedStatisticValue = getCalculatedStatisticValue(singleItemStatistic.value);

      if (calculatedStatisticValue === 0) {
        return initialText;
      }

      formattedText = text.replaceAll(`$${parameterName}`, calculatedStatisticValue.toString());

      formattedText = this.pluralizeValue({
        plurals: plurals,
        text: formattedText,
        value: calculatedStatisticValue.toString(),
        logErrorCb,
      });

      initialText = `${initialText} ${formattedText}`;

      return initialText;
    }

    return initialText;
  };

  private static pluralizeValue = (input: PluralizeInput): string => {
    const {plurals, text, value, logErrorCb} = input;
    let transformedText = text;

    if (plurals.length > 0) {
      const pureNumberValue = value
        .replaceAll(' ', '')
        .replaceAll(' ', '')
        .replaceAll(MILLION_ABBR, '')
        .replaceAll(BILLION_ABBR, '');

      const transformedPureNumberValue = Number(pureNumberValue);

      if (!Number.isNaN(transformedPureNumberValue)) {
        if ((value.includes(MILLION_ABBR) || value.includes(BILLION_ABBR)) && plurals[2]) {
          transformedText = transformedText.replaceAll(MEASURE_TEMPLATE_VALUE, plurals[2]);
        } else {
          const pluralizeValue = declintRu(transformedPureNumberValue, plurals);
          transformedText = transformedText.replaceAll(MEASURE_TEMPLATE_VALUE, pluralizeValue);
        }
      } else {
        // логируем ошибку парсинга числа
        logErrorCb();
      }
    }

    return transformedText;
  };
}
